import { apiClient } from './apiClient.js'
import { buildUrlWithQueryParameters } from '../utils/utils.js'
import { stringifyRecursive } from '../utils/utils.js'

/****************************************************************************
 * 
 * Business Entities
 * 
 ***************************************************************************/
export const businessEntityApi = {
    /**
     * Fetch Business Entities
     * @param {string} rootBusinessEntityId - The Business Entity ID in the hierarchy to start the query from
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @param {string} currentBusinessEntityId - BE ID of the node to return when returnAsTree is true. Defaults to the root
     * @param {bool} includeChildren - Includes nested business entities. Default is true
     * @param {bool} includeParent - Returns the rootBusinessEntityId in the query. Default is true
     */
    fetchBusinessEntity: async (rootBusinessEntityId, config, accessToken, options = { currentBusinessEntityId: null, includeChildren: true, includeParent: true }) => {
        let print = false
        if (print) {
            if (options && "currentBusinessEntityId" in options) {
                console.log("Current business entity ID is ".concat(options.currentBusinessEntityId))
            }
            else {
                console.log("Options:")
                console.log(stringifyRecursive(options))
            }
        }
        if (options && !("includeChildren" in options)) {
            options.includeChildren = true
        }
        if (options && !("includeParent" in options)) {
            options.includeParent = true
        }

        let queryParameters = []
        if (options.includeChildren) {
            queryParameters.push(['includeChildren', options.includeChildren])
        }
        if (options.includeParent) {
            queryParameters.push(['includeParent', options.includeParent])
        }
        if (rootBusinessEntityId) {
            queryParameters.push(['parentBusinessEntityId', rootBusinessEntityId])
        }
        // buildUrlWithQueryParameters takes an array of parameters. Each parameter is a 2 dimensional array. index 0 == key, index 1 == value
        // note that this calls the security service instead of the management api
        const url = buildUrlWithQueryParameters(`${config.securityApiUrl}/businessentitysummaries`, queryParameters)
        return await apiClient(
            url,
            accessToken,
            {
                method: "GET"
            },
            async (response) => {
                return await response.json().then(
                    (result) => {
                        return result.businessEntitySummary
                    }
                )
            }
        )
    },
    validateNewBEAdminEmail: async (config, beId, emailAddress, accessToken) => {
        const url = `${config.accountApiUrl}/adminvalidator?parentBusinessEntityId=${beId}&email=${emailAddress}`
        const options = {
            method: "GET"
        }
        return await apiClient(url, accessToken, options)
    },
    insertOrganization: async (organization, config, accessToken) => {
        const url = `${config.accountApiUrl}/organizations`
        return await apiClient(
            url, 
            accessToken, 
            {
                method: "POST",
                body:JSON.stringify(organization)
            }, 
            async (response) => {
                return await response.json().then(
                    (rt) => {
                        return rt.organization
                    }
                )
            }
        )
    },
    updateOrganization: async (organization, config, accessToken) => {
        //console.log("About to update the following organization:")
        //console.log(organization)
        const url = `${config.accountApiUrl}/organizations/${organization.id}`
        return await apiClient(
            url,
            accessToken,
            {
                method: "POST", //Confirmed: account API requires POST with override for PUT requests
                headers: {
                    'X-HTTP-Method-Override': 'PUT',
                },
                body: JSON.stringify(organization)
            }
        )
    },
    deactivateOrganization: async (businessEntityId, config, accessToken) => {
        const url = `${config.accountApiUrl}/organizations/${businessEntityId}/deactivate`
        return await apiClient(
            url,
            accessToken,
            {
                method: "POST",
                headers: {
                    /*'Content-Length':0,*/
                    'X-HTTP-Method-Override': 'DELETE'
                }
            },
            async (response) => {
                return await response.json().then(
                    (result) => {
                        console.log("Got the following result from deactivating an organization:")
                        console.log(result)
                        return result.organization
                    }
                )
            }
        )
    },
    deleteOrganization: async (businessEntityId, config, accessToken) => {
        const url = `${config.accountApiUrl}/organizations/${businessEntityId}/delete`
        return await apiClient(url, accessToken, {
            method: "POST",
            headers: {
                'X-HTTP-Method-Override': 'DELETE'
            }
        }, null)
    },
    insertMerchant: async (merchant, config, accessToken) => {
        const url = `${config.accountApiUrl}/merchants`
        return await apiClient(
            url, 
            accessToken, 
            {
                method: "POST",
                body:JSON.stringify(merchant)
            }, 
            async (response) => {
                return await response.json().then(
                    (rt) => {
                        return rt.merchant
                    }
                )
            }
        )
    },
    updateMerchant: async (merchant, config, accessToken) => {
        const url = `${config.accountApiUrl}/merchants/${merchant.id}`
        return await apiClient(
            url,
            accessToken,
            {
                method: "POST",
                headers: {
                    'X-HTTP-Method-Override': 'PUT',
                },
                body: JSON.stringify(merchant)
            }
        )
    },
    deleteMerchant: async (businessEntityId, config, accessToken) => {
        const url = `${config.accountApiUrl}/merchants/${businessEntityId}`
        return await apiClient(url, accessToken, {
            method: "POST",
            headers: {
                'X-HTTP-Method-Override': 'DELETE'
            }
        }, null)
    }
}